import Phaser from "phaser";

import { HUD_BOTTOM_RESERVE_PX, HUD_LEFT_RESERVE_W } from "./hud-layout.js";

export type WaypointArrowInset = {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
};

const DEFAULT_WAYPOINT_INSET: WaypointArrowInset = {
  top: 88,
  bottom: HUD_BOTTOM_RESERVE_PX + 16,
  left: HUD_LEFT_RESERVE_W,
  right: 32,
};

export type WaypointArrowApi = {
  setTarget(world: { wx: number; wy: number } | null): void;
  setHidden(hidden: boolean): void;
  update(
    cameraView: { x: number; y: number; width: number; height: number },
    inset?: WaypointArrowInset
  ): void;
  destroy(): void;
};

const FILL = 0x000000;
const FILL_ALPHA = 0.72;
const STROKE = 0xeab308;

export function computeEdgeArrowPlacement(args: {
  cameraView: { x: number; y: number; width: number; height: number };
  worldX: number;
  worldY: number;
  inset?: WaypointArrowInset;
}): { x: number; y: number; angleRad: number; offscreen: boolean } {
  const fallback = 24;
  const insetTop = args.inset?.top ?? fallback;
  const insetBottom = args.inset?.bottom ?? fallback;
  const insetLeft = args.inset?.left ?? fallback;
  const insetRight = args.inset?.right ?? fallback;
  const { x, y, width, height } = args.cameraView;
  const left = x + insetLeft;
  const right = x + width - insetRight;
  const top = y + insetTop;
  const bottom = y + height - insetBottom;
  const cx = x + width / 2;
  const cy = y + height / 2;
  const dx = args.worldX - cx;
  const dy = args.worldY - cy;
  if (
    args.worldX >= x &&
    args.worldX <= x + width &&
    args.worldY >= y &&
    args.worldY <= y + height
  ) {
    return { x: args.worldX, y: args.worldY, angleRad: Math.atan2(dy, dx), offscreen: false };
  }
  const tx = dx === 0 ? Number.POSITIVE_INFINITY : dx > 0 ? (right - cx) / dx : (left - cx) / dx;
  const ty = dy === 0 ? Number.POSITIVE_INFINITY : dy > 0 ? (bottom - cy) / dy : (top - cy) / dy;
  const t = Math.min(tx, ty);
  return {
    x: cx + dx * t,
    y: cy + dy * t,
    angleRad: Math.atan2(dy, dx),
    offscreen: true,
  };
}

export function createWaypointArrow(
  scene: Phaser.Scene,
  opts?: { stroke?: number }
): WaypointArrowApi {
  const tri = scene.add
    .triangle(0, 0, 14, 0, -10, 8, -10, -8, FILL, FILL_ALPHA)
    .setStrokeStyle(3, opts?.stroke ?? STROKE, 1)
    .setDepth(9997)
    .setScrollFactor(0)
    .setVisible(false);

  let target: { wx: number; wy: number } | null = null;
  let hidden = false;

  return {
    setTarget(world) {
      if (
        world &&
        (!Number.isFinite(world.wx) || !Number.isFinite(world.wy))
      ) {
        target = null;
        tri.setVisible(false);
        return;
      }
      target = world;
      if (!world) tri.setVisible(false);
    },
    setHidden(h) {
      hidden = h;
      if (h) tri.setVisible(false);
    },
    update(cameraView, inset) {
      if (!target || hidden) {
        tri.setVisible(false);
        return;
      }
      const place = computeEdgeArrowPlacement({
        cameraView,
        worldX: target.wx,
        worldY: target.wy,
        inset: inset ?? DEFAULT_WAYPOINT_INSET,
      });
      if (!place.offscreen) {
        tri.setVisible(false);
        return;
      }
      const sx = place.x - cameraView.x;
      const sy = place.y - cameraView.y;
      tri.setPosition(sx, sy).setRotation(place.angleRad).setVisible(true);
    },
    destroy() {
      tri.destroy();
    },
  };
}
